import {
  defaultFinancialRateConfig,
  type FinancialRate,
  type FinancialRateConfiguration,
  financialRateToNumber,
  isFinancialRate,
  isFinancialRateInRange,
  newFinancialRate,
  rateToPercentString,
} from '@orus.eu/amount'
import { nbsp } from '@orus.eu/char'
import { DOCUMENTATION_LANGUAGE, type FieldSpecification, m, type ValueDocumentation } from '@orus.eu/message'
import { failure, type Result, success } from '@orus.eu/result'

import {
  AbstractDimension,
  type DimensionMcpAdapter,
  type DimensionOptions,
  type DimensionTrackingMessageFieldAdapter,
  type DimensionValidationProblem,
  dimensionValidationFailure,
  type LooselyTypedValue,
} from './abstract-dimension.ts'

export class FinancialRateDimension<NAME extends string> extends AbstractDimension<NAME, FinancialRate> {
  readonly configuration: FinancialRateConfiguration

  constructor(options: DimensionOptions<NAME, FinancialRate> & { configuration?: FinancialRateConfiguration }) {
    super(options)
    this.configuration = options.configuration ?? defaultFinancialRateConfig
  }

  override validateData(value: LooselyTypedValue): Result<FinancialRate, DimensionValidationProblem> {
    if (typeof value !== 'object' || value === null)
      return dimensionValidationFailure(`Field ${this.name} is not an FinancialRate`)
    if (!('__encodedRate' in value))
      return dimensionValidationFailure(
        `Field ${this.name} is not an FinancialRate because property "__encodedRate" is missing`,
      )
    const { __encodedRate } = value
    if (typeof __encodedRate !== 'number') {
      return dimensionValidationFailure(
        `Field ${this.name} is not an FinancialRate because property "__encodedRate" is not a number`,
      )
    }
    if (!Number.isSafeInteger(__encodedRate)) {
      return dimensionValidationFailure(
        `Field ${this.name} is not an FinancialRate because property "__encodedRate" is not a safe integer`,
      )
    }
    if (!isFinancialRateInRange({ __encodedRate }, this.configuration)) {
      return dimensionValidationFailure(
        `Field ${this.name} is not an FinancialRate because property "__encodedRate" is not in range`,
      )
    }
    return success({ __encodedRate })
  }

  override formatToString(value: FinancialRate): string {
    return rateToPercentString(value, { decimals: 2 })
  }

  override readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter = {
    getTrackingFieldSpecification: (): FieldSpecification => {
      const valueDocumentation: ValueDocumentation<number> = {
        title: this.displayNames[DOCUMENTATION_LANGUAGE],
      }
      if (this.hints) {
        valueDocumentation.detailedExplanationMd = this.hints[DOCUMENTATION_LANGUAGE]
      }
      return m.nullable(m.number(valueDocumentation))
    },
    convertStateValueToTrackingValue: (stateValue: FinancialRate | null | undefined): number | null => {
      if (!stateValue) return null
      return financialRateToNumber(stateValue)
    },
  }

  override readonly mcpAdapter: DimensionMcpAdapter<FinancialRate> = {
    parseValueFromMcp(mcpValueInput: string): Result<FinancialRate, string> {
      if (!mcpValueInput.endsWith('%')) {
        return failure(
          `Invalid input format: ${mcpValueInput}. Please give the rate as a percentage value, including the${nbsp}% sign at the end.`,
        )
      }
      const inputWithoutPercentSign = mcpValueInput.slice(0, -1)
      const floatValue = parseFloat(inputWithoutPercentSign)
      if (isNaN(floatValue)) {
        return failure(
          `Invalid input format: ${mcpValueInput}. Please give the rate as a percentage value, including the${nbsp}% sign at the end.`,
        )
      }
      const rate = newFinancialRate(floatValue / 100)
      return success(rate)
    },
    formattingInstructions: 'Specify a number in the simplest format possible.',
  }
}

export class FinancialRateWithUnsetDimension<NAME extends string> extends AbstractDimension<
  NAME,
  FinancialRate | 'unset'
> {
  readonly configuration: FinancialRateConfiguration

  constructor(
    options: DimensionOptions<NAME, FinancialRate | 'unset'> & { configuration?: FinancialRateConfiguration },
  ) {
    super(options)
    this.configuration = options.configuration ?? defaultFinancialRateConfig
  }

  override validateData(value: LooselyTypedValue): Result<FinancialRate | 'unset', DimensionValidationProblem> {
    if (value === 'unset') return success('unset')

    if (typeof value !== 'object' || value === null)
      return dimensionValidationFailure(`Field ${this.name} is not an FinancialRate`)
    if (!('__encodedRate' in value))
      return dimensionValidationFailure(
        `Field ${this.name} is not an FinancialRate because property "__encodedRate" is missing`,
      )
    const { __encodedRate } = value
    if (typeof __encodedRate !== 'number') {
      return dimensionValidationFailure(
        `Field ${this.name} is not an FinancialRate because property "__encodedRate" is not a number`,
      )
    }
    if (!Number.isSafeInteger(__encodedRate)) {
      return dimensionValidationFailure(
        `Field ${this.name} is not an FinancialRate because property "__encodedRate" is not a safe integer`,
      )
    }

    if (!isFinancialRateInRange({ __encodedRate }, this.configuration)) {
      return dimensionValidationFailure(
        `Field ${this.name} is not an FinancialRate because property "__encodedRate" is not in range`,
      )
    }

    return success({ __encodedRate })
  }

  override formatToString(value: FinancialRate | 'unset'): string | undefined {
    if (value === 'unset') {
      return 'unset'
    }

    return rateToPercentString(value, { decimals: 2 })
  }

  override readonly trackingMessageFieldAdapter: DimensionTrackingMessageFieldAdapter = {
    getTrackingFieldSpecification: (): FieldSpecification => {
      const valueDocumentation: ValueDocumentation<number> = {
        title: this.displayNames[DOCUMENTATION_LANGUAGE],
      }
      if (this.hints) {
        valueDocumentation.detailedExplanationMd = this.hints[DOCUMENTATION_LANGUAGE]
      }
      return m.nullable(m.number(valueDocumentation))
    },
    convertStateValueToTrackingValue: (stateValue: FinancialRate | 'unset' | null | undefined): number | null => {
      if (!stateValue || stateValue === 'unset') return null
      return financialRateToNumber(stateValue)
    },
  }
}

/**
 * Check if the value is a valid FinancialRate or 'unset'.
 */
export function isFinancialRateWithUnsetFullyFilled(value: unknown): boolean {
  return value === 'unset' || isFinancialRate(value)
}
